import { createRef, LitElement, css, html, ref } from '@lit'
import styles from '@nova/styles'

class NovaSearchResult extends LitElement {
  static properties = {
    searchData: { type: String },
    searchTerm: { type: String },
  }

  constructor() {
    super()
    this.passedFirstRender = false
    this.searchData = ''
    this.searchTerm = ''
    this.searchResultContainerRef = createRef()
    this.noResultAbortController = null
    this.noResultRequestKey = 0
    this._shouldLoadNoResultOnDrawerOpen = false
    this.handleDrawerOpen = this.handleDrawerOpen.bind(this)
  }

  connectedCallback() {
    super.connectedCallback()
    window.addEventListener('nova:header-drawer:open', this.handleDrawerOpen)
  }

  getSearchDrawer() {
    return this.getRootNode()?.host?.closest('.js-header-drawer')
  }

  handleDrawerOpen(event) {
    const searchDrawer = this.getSearchDrawer()
    const openedDrawer = event?.detail?.target

    if (
      !searchDrawer ||
      openedDrawer !== searchDrawer ||
      openedDrawer?.dataset?.type !== 'search'
    ) {
      return
    }

    if (this.searchTerm || this.passedFirstRender) return

    const container = this.searchResultContainerRef.value
    if (!container) {
      this._shouldLoadNoResultOnDrawerOpen = true
      return
    }

    this.handleNoResult(container)
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    this.cancelNoResultRequest()
    window.removeEventListener('nova:header-drawer:open', this.handleDrawerOpen)
  }

  cancelNoResultRequest() {
    this.noResultAbortController?.abort()
    this.noResultAbortController = null
  }

  firstUpdated(changedProperties) {
    super.firstUpdated(changedProperties)
    const container = this.searchResultContainerRef.value
    if (!container) return

    if (!this.searchTerm && !this.passedFirstRender) {
      if (this._shouldLoadNoResultOnDrawerOpen) {
        this._shouldLoadNoResultOnDrawerOpen = false
        this.handleNoResult(container)
        return
      }

      const searchDrawer = this.getSearchDrawer()
      if (searchDrawer?.hasAttribute('is-open')) {
        this.handleNoResult(container)
      }
    }
  }

  updated(changedProperties) {
    super.updated(changedProperties)
    if (this.searchData === '') {
      this.cancelNoResultRequest()
      const container = this.searchResultContainerRef.value
      if (container) {
        const noResultElement = container.querySelector('.js-suggested-results')
        const predictiveElement = container.querySelector('.js-predictive-search')
        const hasNoResultHeader = Boolean(
          noResultElement?.querySelector('.suggested-results__header'),
        )

        if (predictiveElement || hasNoResultHeader) {
          this.handleNoResult(container)
        } else if (!noResultElement && this.passedFirstRender) {
          this.handleNoResult(container)
        }
      }
      return
    }

    if (changedProperties.has('searchData')) {
      this.cancelNoResultRequest()

      const parser = new DOMParser()
      const doc = parser.parseFromString(this.searchData, 'text/html')
      const searchResultElement = doc.querySelector('.js-predictive-search')

      if (!searchResultElement) return

      const container = this.searchResultContainerRef.value
      const searchResultCount = searchResultElement.querySelector('.js-predictive-search-count')?.dataset?.amount

      if (searchResultCount && Number(searchResultCount) >0 ) {
        container.innerHTML = ''
        container.appendChild(searchResultElement)

        this.updateProductCards()
      } else {
        this.handleNoResult(container)
      }
    }
  }

  async handleNoResult(container) {
    this.cancelNoResultRequest()

    const requestKey = ++this.noResultRequestKey
    const expectedSearchData = this.searchData
    const expectedSearchTerm = this.searchTerm
    const abortController = new AbortController()
    this.noResultAbortController = abortController

    try {
      const response = await fetch(
        `/search?q=${encodeURIComponent(expectedSearchTerm)}&section_id=${window.main_suggested_result_section_id}`,
        { signal: abortController.signal },
      )
      if (!response.ok) {
        if (abortController.signal.aborted || requestKey !== this.noResultRequestKey) return
        console.error(`HTTP error! Status: ${response.status}`)
        console.error(`HTTP error! Reason: ${response}`)
        container.innerHTML = ''
        return
      }
      const data = await response.text()
      const parser = new DOMParser()
      const doc = parser.parseFromString(data, 'text/html')
      const noResultElement = doc.querySelector('.js-suggested-results')

      if (
        abortController.signal.aborted ||
        requestKey !== this.noResultRequestKey ||
        expectedSearchData !== this.searchData ||
        expectedSearchTerm !== this.searchTerm
      ) {
        return
      }

      container.innerHTML = ''
      if (!noResultElement) return

      container.appendChild(noResultElement)
      this.passedFirstRender = true

      this.updateProductCards()
    } catch (error) {
      if (error.name === 'AbortError') return
      if (abortController.signal.aborted || requestKey !== this.noResultRequestKey) return
      console.error('Error:', error.message)
      container.innerHTML = ''
      return
    } finally {
      if (this.noResultAbortController === abortController) {
        this.noResultAbortController = null
      }
    }
  }

  updateProductCards() {
    const container = this.searchResultContainerRef.value

    const cards = container.querySelectorAll('nova-product-card')
    for (const card of cards) {
      const variantsTemplate = container.querySelector(
        `#async-variants-json-${card.id}`,
      )
      const variants = variantsTemplate
        ? JSON.parse(variantsTemplate.content.textContent.trim())
        : []
      card.variants = variants

      const swatchesTemplate = container.querySelector(
        `#async-swatches-json-${card.id}`,
      )
      const swatches = swatchesTemplate
        ? JSON.parse(swatchesTemplate.content.textContent.trim())
        : []
      card.swatches = swatches

      const sizesTemplate = container.querySelector(
        `#async-sizes-json-${card.id}`,
      )
      const sizes = sizesTemplate
        ? JSON.parse(sizesTemplate.content.textContent.trim())
        : []
      card.sizes = sizes

      const variantUrlsTemplate = container.querySelector(
        `#async-variant-urls-json-${card.id}`,
      )
      const variantUrls = variantUrlsTemplate
        ? JSON.parse(variantUrlsTemplate.content.textContent.trim())
        : []
      card.variantUrls = variantUrls
    }
  }

  render() {
    return html`
      <div ${ref(this.searchResultContainerRef)}></div>
    `
  }

   static styles = [
    styles.flex,
    styles.variables,
    styles.typography,
    // When container.appendChild(searchResultElement)
    css`
      .predictive-search mark {
        font-weight: 700;
        color: inherit;
        background-color: inherit;
      }

      .predictive-search a:hover {
        text-decoration: underline;
      }

      .predictive-search a {
        text-decoration: none;
        color: inherit;
        font: var(--nova-body-font);
      }

      .predictive-search span {
        color: inherit;
        font: var(--nova-body-font);
      }

      .predictive-search__content {
        padding: 30px 16px 0 16px;
      }

      .predictive-search__content-inner {
        flex-direction: column;
        row-gap: var(--nova-space-xl);
        padding-bottom: 30px;
      }

      .predictive-search__items {
        display: flex;
        flex-direction: column;
        row-gap: 30px;
      }

      .predictive-search__queries,
      .predictive-search__products {
        row-gap: 18px;

        & .predictive-search__heading {
          font-size: 14px;
          font-weight: 400;
          line-height: 15px;
          letter-spacing: 0.28px;
          color: var(--nova-color-grey-350);
          margin: 0;
          padding: 0;
        }
      }

      .predictive-search__view-all-link {
        font-size: 14px;
        font-weight: 400;
        line-height: 15px;
        letter-spacing: 0.28px;
        color: var(--nova-brand-ink);
        text-transform: capitalize;
        text-decoration: underline !important;
      }

      .predictive-search__queries-list {
        display: flex;
        flex-direction: column;
        row-gap: 14px;
      }

      .predictive-search__queries-list a {
        font-size: 14px;
        font-weight: 400;
        line-height: 15px;
        letter-spacing: 0.28px;
        color: var(--nova-brand-ink);
        text-transform: capitalize;
        text-decoration: none;

        &:hover {
          text-decoration: underline;
          color: var(--nova-brand-ink);
        }
      }

      .predictive-search__products {
        flex: 1;
      }

      .predictive-search__products-list {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        grid-template-rows: auto;
        grid-column-gap: var(--nova-space-2xs);
        grid-row-gap: var(--nova-space-lg-1);

        & .predictive-search__product-card {
          & nova-product-card {
            padding-bottom: 0;

            &::part(product-header-element) {
              margin-bottom: 0;
              flex-direction: column;
              gap: var(--nova-space-4xs);
            }

            &::part(product-title-element) {
              display: block;
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
            }
          }
        }
      }

      @media screen and (min-width: 990px) {
        .predictive-search__content {
          padding: 40px 32px 0 32px;
        }

        .predictive-search__content-inner {
          padding-bottom: 40px;
        }

        .predictive-search__products-list {
          grid-template-columns: repeat(4, 1fr);
          grid-column-gap: var(--nova-space-xs-1);
          grid-row-gap: var(--nova-space-xs-1);
        }
      }

      @media screen and (min-width: 1200px) {
        .predictive-search__products {
          max-width: 72%;
          flex: 1;
        }

        .predictive-search__content-inner {
          flex-direction: row;
          column-gap: 48px;
          justify-content: space-between;
        }

        .predictive-search__queries,
        .predictive-search__products {
          row-gap: 20px;
        }

        .predictive-search__queries-list {
          row-gap: 20px;
        }
      }

      .predictive-search__product-card {
        width: 100%;
        min-width: 0;
        justify-self: stretch;
      }
    `,
    // When no results
    css`
      .suggested-results {
        padding: 30px 16px 0 16px;
      }

      .suggested-results__content-wrapper {
        flex-direction: column;
        row-gap: var(--nova-space-lg);
        padding-bottom: 30px;
      }

      .suggested-results a {
        text-decoration: none;
      }

      .suggested-results a:hover {
        text-decoration: underline;
        color: var(--nova-brand-ink);
      }

      .suggested-results p {
        margin: 0;
      }

      .suggested-results__header {
        row-gap: 5px;
      }

      .suggested-results__heading {
        font: var(--nova-body-font);
        line-height: 15px;
        letter-spacing: 0.28px;
        color: var(--nova-color-grey-350);
        margin: 0;
        padding: 0;
      }

      .suggested-results__terms-list {
        gap: 14px;
      }

      .suggested-results__terms-list a {
        font: var(--nova-body-font);
        line-height: 15px;
        letter-spacing: 0.28px;
        color: var(--nova-brand-ink);
        text-transform: capitalize;
      }

      .suggested-results__header strong {
        font-weight: bolder;
      }

      .suggested-results__sub-header {
        font-size: 12px;
        line-height: 1.4;
        color: var(--nova-color-subdued);
      }

      .suggested-results__content {
        row-gap: 30px;
      }

      .suggested-results__products,
      .suggested-results__terms {
        gap: 18px;
      }

      .suggested-results__products-list {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        grid-template-rows: auto;
        grid-column-gap: var(--nova-space-2xs);
        grid-row-gap: var(--nova-space-2xs);
      }

      @media screen and (min-width: 990px) {
        .suggested-results {
          padding: 40px 32px 0 32px;
        }

        .suggested-results__products-list {
          grid-template-columns: repeat(4, 1fr);
        }
      }

      @media screen and (min-width: 1200px) {
        .suggested-results__content-wrapper {
          flex-direction: row;
          justify-content: space-between;
          padding-bottom: 40px;
        }

        .suggested-results__products {
          max-width: 72%;
          flex: 1;
        }

        .suggested-results__products,
        .suggested-results__terms {
          gap: 18px;
        }

        .suggested-results__terms-list {
          gap: 20px;
        }
      }

      .suggested-results__product-card {
        width: 100%;
        min-width: 0;
        justify-self: stretch;

        &::part(product-header-element) {
          margin-bottom: 0;
          flex-direction: column;
          gap: var(--nova-space-4xs);
        }
      }
    `,
    css `
      .search-drawer-footer {
        flex-direction: column;
        row-gap: 18px;
        padding: 30px 0;
        border-top: 2px solid var(--nova-section-color-secondary);
      }

      .search-drawer-footer__heading {
        font: var(--nova-body-font);
        line-height: 15px;
        letter-spacing: 0.28px;
        color: var(--nova-color-grey-350);
        margin: 0;
        padding: 0;
      }

      .search-drawer-footer__menu {
        display: flex;
        flex-direction: column;
        row-gap: 14px;
        padding: 0;
        margin: 0;
      }

      .search-drawer-footer__menu-item {
        padding: 0;
        margin: 0;
        list-style: none;
      }

      .search-drawer-footer__menu-link {
        font: var(--nova-body-font);
        line-height: 15px;
        letter-spacing: 0.28px;
        color: var(--nova-brand-ink);
        text-transform: capitalize;
        text-decoration: none;

        &:hover {
          text-decoration: underline;
          color: var(--nova-brand-ink);
        }
      }

      @media screen and (min-width: 1200px) {
        .search-drawer-footer {
          column-gap: 20px;
          flex-direction: row;
          align-items: center;
        }

        .search-drawer-footer__menu {
          flex-direction: row;
          row-gap: 0;
          column-gap: 20px;
        }
      }
    `
   ]
}

customElements.define('nova-search-result', NovaSearchResult)
